import { useState } from "react";
import { DateRange } from "react-day-picker";
import { addDays } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { MetricsCards } from "./components/MetricsCards";
import { ActivityChart } from "./components/ActivityChart";
import { DetailsTable } from "./components/DetailsTable";
import { DateRangeFilter } from "./components/DateRangeFilter";
import { Loader2 } from "lucide-react";

const ReportsPage = () => {
  const [date, setDate] = useState<DateRange | undefined>({
    from: addDays(new Date(), -30),
    to: new Date(),
  });

  const { data: reportData, isLoading } = useQuery({
    queryKey: ['reports', date],
    queryFn: async () => {
      const [sendingsResult, propertiesResult, ownersResult, schedulesResult] = await Promise.all([
        supabase.from('messages').select('*'),
        supabase.from('properties').select('*'),
        supabase.from('profiles').select('*'),
        supabase.from('schedules').select('*')
      ]);

      const sendings = {
        total: sendingsResult.data?.length || 0,
        successful: sendingsResult.data?.filter(m => m.status === 'sent').length || 0,
        failed: sendingsResult.data?.filter(m => m.status === 'error').length || 0,
        scheduled: sendingsResult.data?.filter(m => m.status === 'scheduled').length || 0,
      };

      const properties = {
        total: propertiesResult.data?.length || 0,
        available: propertiesResult.data?.filter(p => p.status === 'available').length || 0,
        rented: propertiesResult.data?.filter(p => p.status === 'rented').length || 0,
        sold: propertiesResult.data?.filter(p => p.status === 'sold').length || 0,
      };

      const owners = {
        total: ownersResult.data?.length || 0,
        active: ownersResult.data?.filter(o => o.active).length || 0,
        inactive: ownersResult.data?.filter(o => !o.active).length || 0,
      };

      const schedules = {
        total: schedulesResult.data?.length || 0,
        completed: schedulesResult.data?.filter(s => s.status === 'completed').length || 0,
        pending: schedulesResult.data?.filter(s => s.status === 'pending').length || 0,
      };

      // Calculate chart data based on messages and schedules
      const chartData = Array.from({ length: 6 }, (_, i) => {
        const month = new Date();
        month.setMonth(month.getMonth() - i);
        const monthStr = month.toLocaleString('default', { month: 'short' });
        
        const monthMessages = sendingsResult.data?.filter(m => {
          const msgDate = new Date(m.created_at);
          return msgDate.getMonth() === month.getMonth();
        }).length || 0;

        const monthSchedules = schedulesResult.data?.filter(s => {
          const schDate = new Date(s.created_at);
          return schDate.getMonth() === month.getMonth();
        }).length || 0;

        return {
          month: monthStr,
          mensagens: monthMessages,
          agendamentos: monthSchedules,
        };
      }).reverse();

      return {
        sendings,
        properties,
        owners,
        schedules,
        chartData,
      };
    }
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 space-y-8 max-w-7xl animate-fade-in">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-sm p-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div>
            <h1 className="text-3xl lg:text-4xl font-heading font-bold bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">
              Relatórios
            </h1>
            <p className="mt-2 text-base lg:text-lg text-gray-600">
              Visualize relatórios e análises detalhadas do seu negócio
            </p>
          </div>
          
          <DateRangeFilter date={date} onDateChange={setDate} />
        </div>
      </div>

      <div className="grid gap-8">
        {reportData && (
          <>
            <MetricsCards data={reportData} />
            <div className="grid lg:grid-cols-2 gap-8">
              <ActivityChart data={reportData.chartData} />
              <DetailsTable data={reportData} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;